import { type PointTooltipProps } from '@nivo/line'
import { Card, Divider, Stack, styled } from '@mui/material'
import { ContentChip, isoDateStringToLocalFormat, RectangleIcon, tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { type LineChartSeries } from './types'

export type LineChartTooltipCopy = {
    draft: string
    date: string
    year: string
}

const DEFAULT_LINE_CHART_TOOLTIP_COPY: LineChartTooltipCopy = {
    draft: 'Entwurf',
    date: 'Datum',
    year: 'Jahr',
}

export type LineChartTooltipTranslations = {
    draftLabel?: string
    yearLabel?: string
    dateLabel?: string
}

type LineChartTooltipProps = PointTooltipProps<LineChartSeries> & {
    copy?: Partial<LineChartTooltipCopy>
    translations?: LineChartTooltipTranslations
    locale?: string
}

export function LineChartTooltip({ point, copy, translations, locale = 'de-DE' }: LineChartTooltipProps) {
    const resolvedCopy: LineChartTooltipCopy = {
        draft: translations?.draftLabel ?? copy?.draft ?? DEFAULT_LINE_CHART_TOOLTIP_COPY.draft,
        date: translations?.dateLabel ?? copy?.date ?? DEFAULT_LINE_CHART_TOOLTIP_COPY.date,
        year: translations?.yearLabel ?? copy?.year ?? DEFAULT_LINE_CHART_TOOLTIP_COPY.year,
    }
    // move tooltip to the bottom of the mouse if it is cut off at the top
    const transformY = point.y > 50 ? '0' : '150%'
    const yearOnly = point.data.yearOnly

    return (
        <StyledTooltip $transformY={transformY}>
            <Stack direction="row" gap={tokens.SizingSpacing050} alignItems="baseline">
                <ColoredBox $color={point.seriesColor}>
                    <RectangleIcon height={tokens.SizingIconS} width={tokens.SizingIconS} />
                </ColoredBox>
                <Typography variant="textXS" color={tokens.ColorTextDefault}>
                    {point.seriesId}:
                </Typography>
                <Typography variant="textXS">{point.data.yFormatted}</Typography>
            </Stack>
            <Divider />
            <Stack direction="row" gap={tokens.SizingSpacing050} alignItems="baseline">
                {point.data.isPrediction && <ContentChip color="orange" size="small" label={resolvedCopy.draft} />}
                <Typography variant="textXS" color={tokens.ColorTextDefault}>
                    {yearOnly ? `${resolvedCopy.year}: ` : `${resolvedCopy.date}: `}
                </Typography>
                <Typography variant="textXS">
                    {yearOnly ? point.data.xFormatted : isoDateStringToLocalFormat(point.data.date, false, locale)}
                </Typography>
            </Stack>
        </StyledTooltip>
    )
}

const StyledTooltip = styled(Card, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})(({ $transformY }: { $transformY: string }) => ({
    background: tokens.ColorSurfaceDefault,
    padding: '6px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.SizingSpacing050,
    transform: `translate(0, ${$transformY})`,
    lineHeight: '1.4',
}))

const ColoredBox = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})(({ $color }: { $color: string }) => ({
    color: $color,
    transform: 'translateY(3px)',
}))
